'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import { toast } from 'sonner';

import Box from '@mui/material/Box';

import { GQLMutation } from '@/lib/client';
import { USER_GOOGLE_AUTH } from '@/lib/mutations/user.mutation';
import { useTranslate } from '@/locales/use-locales';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential?: string }) => void;
          }) => void;
          renderButton: (
            element: HTMLElement,
            options: {
              type: 'standard';
              theme: 'outline';
              size: 'large';
              text: 'continue_with';
              shape: 'rectangular';
              width: number;
            }
          ) => void;
        };
      };
    };
  }
}

const GOOGLE_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

export function GoogleAuthButton() {
  const { t } = useTranslate();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [scriptReady, setScriptReady] = useState(false);
  const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
  const { action: authenticate, loading } = GQLMutation({
    mutation: USER_GOOGLE_AUTH,
    resolver: 'googleAuth',
    toastmsg: true,
    callback: () => window.location.assign('/'),
  });

  const renderButton = useCallback(() => {
    const button = buttonRef.current;
    if (!clientId || !button || !window.google) return;

    button.replaceChildren();
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: ({ credential }) => {
        if (!credential) {
          toast.error(t('auth.google.failed'));
          return;
        }
        authenticate({ variables: { input: { idToken: credential } } });
      },
    });
    window.google.accounts.id.renderButton(button, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      width: Math.min(button.clientWidth || 320, 400),
    });
  }, [authenticate, clientId, t]);

  useEffect(() => {
    if (!scriptReady || !buttonRef.current) return undefined;

    renderButton();
    window.addEventListener('resize', renderButton);
    return () => window.removeEventListener('resize', renderButton);
  }, [renderButton, scriptReady]);

  if (!clientId) return null;

  return (
    <>
      <Script
        id="google-identity-services"
        src={GOOGLE_SCRIPT_URL}
        strategy="afterInteractive"
        onReady={() => setScriptReady(true)}
        onError={() => toast.error(t('auth.google.unavailable'))}
      />
      <Box
        aria-busy={loading}
        sx={{
          width: '100%',
          minHeight: 44,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: loading ? 'none' : 'auto',
          opacity: loading ? 0.6 : 1,
        }}
      >
        <Box ref={buttonRef} sx={{ width: '100%', maxWidth: 400 }} />
      </Box>
    </>
  );
}
